import { useState, useRef, useEffect, useCallback } from "react";
import "./LivePopout.css";

function getIsMobile() {
  return typeof window !== "undefined" && window.innerWidth < 768;
}

export default function LivePopout({ src = "https://www.tiktok.com/embed/live/@csr2640" }) {
  const [minimized, setMinimized] = useState(() => getIsMobile());
  const [position, setPosition] = useState(() => ({
    x: getIsMobile() ? 8 : window.innerWidth - 380,
    y: getIsMobile() ? window.innerHeight - 80 : 150,
  }));
  const [size, setSize] = useState({ w: 360, h: 260 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const popoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (getIsMobile()) {
        setPosition({ x: 8, y: window.innerHeight - 80 });
        setSize({ w: Math.min(360, window.innerWidth - 24), h: 220 });
      } else {
        setPosition({ x: window.innerWidth - 380, y: 150 });
        setSize({ w: 360, h: 260 });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getClientPos = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handleDragStart = useCallback((e) => {
    if (e.target.tagName === "BUTTON" || e.target.tagName === "IFRAME") return;
    const pos = getClientPos(e);
    setDragging(true);
    dragOffset.current = {
      x: pos.x - position.x,
      y: pos.y - position.y,
    };
  }, [position]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getClientPos(e);
    setResizing(true);
    resizeStart.current = { x: pos.x, y: pos.y, w: size.w, h: size.h };
  }, [size]);

  useEffect(() => {
    const handleMove = (e) => {
      const pos = getClientPos(e);
      if (dragging) {
        e.preventDefault();
        setPosition({
          x: pos.x - dragOffset.current.x,
          y: pos.y - dragOffset.current.y,
        });
      }
      if (resizing) {
        e.preventDefault();
        const dx = pos.x - resizeStart.current.x;
        const dy = pos.y - resizeStart.current.y;
        setSize({
          w: Math.max(200, resizeStart.current.w + dx),
          h: Math.max(120, resizeStart.current.h + dy),
        });
      }
    };

    const handleEnd = () => {
      setDragging(false);
      setResizing(false);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [dragging, resizing]);

  return (
    <div
      ref={popoutRef}
      className={`live-popout ${minimized ? "live-popout--minimized" : ""}`}
      style={{
        left: position.x,
        top: position.y,
        width: minimized ? 160 : size.w,
      }}
    >
      <div
        className="live-popout-bar"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <span className="live-popout-title">LIVE</span>
        <div className="live-popout-buttons">
          <button
            className="live-popout-btn"
            onClick={() => setMinimized(!minimized)}
          >
            {minimized ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              "\u2014"
            )}
          </button>
        </div>
      </div>
      {!minimized && (
        <div className="live-popout-content" style={{ height: size.h }}>
          <iframe
            title="TikTok Live"
            src={src}
            allowFullScreen
          />
          <div
            className="live-popout-resize"
            onMouseDown={handleResizeStart}
            onTouchStart={handleResizeStart}
          />
        </div>
      )}
    </div>
  );
}
